import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";
import { toast } from "sonner";
import {
  Award, Sparkles, TrendingUp, TrendingDown, Shield, AlertTriangle,
  CheckCircle2, Target, Zap, Users, GraduationCap, MessageSquare, ShieldAlert,
  ArrowRight, RefreshCw, Loader2, ChevronRight, Download, ExternalLink, Building2, Info,
} from "lucide-react";

const AXIS_LABELS = {
  patrimoine: "Patrimoine",
  emploi: "Emploi",
  employabilite: "Employabilité",
  education: "Éducation",
  entrepreneuriat: "Entrepreneuriat",
  reseau: "Réseau",
  resilience: "Résilience",
  autonomie: "Autonomie",
};

const INDEX_LABELS = {
  idx_mobility: "Mobilité",
  idx_resilience: "Résilience",
  idx_stability: "Stabilité",
  idx_autonomie: "Autonomie",
  idx_potential: "Potentiel",
  idx_risk: "Risque",
};

export default function DiagnosticReport() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [matches, setMatches] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [comprehension, setComprehension] = useState(null);
  const [showExplain, setShowExplain] = useState(false);

  useEffect(() => { load(); }, []); // load on mount only

  // Auto-refresh while background AI analysis is running (P1 async job queue)
  useEffect(() => {
    if (!data) return;
    const diag = data.diagnostic || {};
    const status = diag.analysis_status;
    const hasAi = diag.ai_analysis && diag.ai_analysis.generated_at;
    if (hasAi || status === "completed" || status === "failed") return;
    // Poll every 3s for up to 3 minutes
    let attempts = 0;
    const interval = setInterval(async () => {
      attempts += 1;
      if (attempts > 60) { clearInterval(interval); return; }
      try {
        const { data: st } = await api.get("/diagnostic/me/analyze/status");
        if (st.analysis_status === "completed" || st.analysis_status === "failed" || st.has_ai_analysis) {
          clearInterval(interval);
          // Refresh the report + comprehension so UI updates with the narrative
          const { data: fresh } = await api.get("/diagnostic/me/report");
          setData(fresh);
          api.get("/diagnostic/me/comprehension").then((r) => setComprehension(r.data)).catch(() => {});
        }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(interval);
  }, [data]);

  const load = async () => {
    setLoading(true);
    try {
      const { data: resp } = await api.get("/diagnostic/me/report");
      setData(resp);
      // Fetch matches + M1 comprehension in parallel (non-blocking)
      api.get("/diagnostic/me/matches").then((r) => setMatches(r.data.matches)).catch(() => {});
      api.get("/diagnostic/me/comprehension").then((r) => setComprehension(r.data)).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.info("Créez d'abord votre diagnostic.");
        navigate("/app/diagnostic");
      } else {
        toast.error("Impossible de charger le rapport.");
      }
    } finally { setLoading(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/diagnostic/me/pdf", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `altiora-diagnostic-imes.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapport PDF téléchargé.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Téléchargement PDF impossible.");
    } finally { setDownloading(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#F8F7F3]"><TopBar />
      <div className="max-w-6xl mx-auto px-6 py-14"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>
    </div>
  );
  if (!data) return null;

  const d = data.diagnostic;
  const s = d.scores;
  const scenarios = d.scenarios || [];
  const ai = d.ai_analysis || {};

  if (!s) {
    return (
      <div className="min-h-screen bg-[#F8F7F3]"><TopBar />
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-14">
          <div className="border-2 border-amber-300 bg-amber-50 rounded-lg p-8 text-center">
            <Sparkles className="h-10 w-10 mx-auto text-amber-700 mb-3" />
            <h2 className="font-display text-2xl text-amber-900 mb-2">Diagnostic non terminé</h2>
            <p className="text-sm text-amber-800 mb-5">Complétez toutes les sections et lancez l&apos;analyse pour voir votre rapport IMES™.</p>
            <Link to="/app/diagnostic" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-700 text-white px-5 py-2.5 rounded-md font-medium text-sm">
              Continuer le diagnostic <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const gradeColor = { "A+": "text-emerald-700 bg-emerald-100", "A": "text-emerald-700 bg-emerald-100",
    "B+": "text-blue-700 bg-blue-100", "B": "text-blue-700 bg-blue-100",
    "C+": "text-amber-700 bg-amber-100", "C": "text-amber-700 bg-amber-100",
    "D": "text-red-700 bg-red-100" }[s.imes_grade] || "text-navy bg-navy-50";

  return (
    <div className="min-h-screen bg-[#F8F7F3]" data-testid="diagnostic-report">
      <TopBar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10 md:py-14">
        {/* Header narratif */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0, transition: { duration: 0.6 } }}
          className="flex items-start justify-between mb-10 gap-6 flex-wrap"
        >
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 mb-3">
              <Award className="h-4 w-4 text-[#7A5B14]" />
              <div className="text-xs font-mono uppercase tracking-[0.28em] text-navy">Rapport ALTIORA · IMES™ v2</div>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-navy leading-[1.05]">
              Voici ce que nous avons entendu.
            </h1>
            <p className="mt-5 text-base md:text-lg text-[#374151] leading-relaxed">
              Analyse consolidée sur les 7 couches de votre trajectoire — origine, fondations, économie, patrimoine, réseau, bien-être, aspirations.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={load} className="inline-flex items-center gap-2 border-2 border-[#E5E7EB] text-[#374151] hover:border-navy hover:text-navy px-4 py-2.5 text-sm font-medium rounded-md transition">
              <RefreshCw className="h-4 w-4" /> Actualiser
            </button>
            <button
              onClick={downloadPdf}
              disabled={downloading}
              data-testid="report-pdf-btn"
              className="inline-flex items-center gap-2 border-2 border-[#C5A880] bg-[#FBF6EA] text-[#7A5B14] hover:bg-[#F5EBD3] px-4 py-2.5 text-sm font-medium rounded-md transition disabled:opacity-60"
            >
              {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              PDF Premium
            </button>
            <Link to="/app/diagnostic" data-testid="report-edit-btn" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-700 text-white px-4 py-2.5 text-sm font-medium rounded-md shadow-sm transition">
              Modifier le diagnostic <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>

        {/* Async AI analysis running banner */}
        {(d.analysis_status === "running" || d.analysis_status === "queued") && !ai?.generated_at && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0, transition: { duration: 0.5 } }}
            className="mb-6 rounded-md border border-[#C5A880] bg-[#FBF6EA] px-4 py-3 flex items-center gap-3"
            data-testid="report-ai-running-banner"
          >
            <Loader2 className="h-4 w-4 text-[#7A5B14] animate-spin" />
            <div className="text-sm text-[#5A4308]">
              L&apos;analyse Claude Sonnet se termine en arrière-plan — la lecture narrative apparaîtra automatiquement dans quelques secondes.
            </div>
          </motion.div>
        )}

        {/* IMES Hero — révélation cinématique */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0, transition: { delay: 0.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] } }}
          className="border-2 border-[#0B2545]/25 bg-white rounded-xl p-8 md:p-10 mb-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-10 items-center"
          data-testid="report-imes-card"
        >
          <div className="text-center">
            <div className="text-xs font-mono uppercase tracking-widest text-[#7A5B14] mb-2">Indice IMES™</div>
            <motion.div
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1, transition: { delay: 0.4, duration: 1.1, ease: [0.22, 1, 0.36, 1] } }}
              className="font-display text-7xl md:text-[7rem] text-navy leading-none tabular-nums"
            >
              {s.imes}
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1, transition: { delay: 0.9, duration: 0.6 } }}
              className={`inline-block mt-4 px-4 py-1.5 text-sm font-bold rounded-md ${gradeColor}`}
            >
              {s.imes_grade}
            </motion.div>
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-[#8B8B8F]">
              sur 100 · méthode {s.method_version || "v1"}
            </div>
          </div>
          <div>
            <h2 className="font-display text-xl text-navy mb-2">Décomposition IMES v2</h2>
            <p className="text-xs text-[#5B5B62] mb-5">
              4 composantes pondérées selon la Doctrine ALTIORA Méthode™ v2.
            </p>
            <div className="space-y-3">
              <DimensionBar label="Origine — d'où vous venez" value={s.origine_score ?? s.upstream_score} weight="20%" tone="from-amber-400 to-amber-600" />
              <DimensionBar label="Présent — situation composite" value={s.present_composite ?? s.current_score} weight="40%" tone="from-navy to-blue-700" />
              <DimensionBar label="Trajectoire — direction projetée" value={s.trajectoire_score ?? s.downstream_score} weight="30%" tone="from-emerald-400 to-emerald-600" />
              <DimensionBar label="Résilience — le maillon le plus faible" value={s.resilience_multi_axes ?? 0} weight="10%" tone="from-rose-400 to-rose-600" />
            </div>
            {comprehension && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0, transition: { delay: 1.1, duration: 0.6 } }}
                className="mt-5 p-4 bg-[#F1EFE9] rounded-lg border border-[#E4E1D8]"
              >
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14] mb-1">
                  Lecture ALTIORA · M1 Compréhension
                </div>
                <p className="text-sm text-[#374151] leading-relaxed">
                  {comprehension.answer?.narrative_fr}
                </p>
                <button
                  onClick={() => setShowExplain((v) => !v)}
                  data-testid="report-explain-toggle"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs text-navy hover:underline"
                >
                  <Info className="h-3 w-3" />
                  {showExplain ? "Masquer" : "Pourquoi ce score ?"}
                </button>
                {showExplain && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto", transition: { duration: 0.4 } }}
                    className="mt-3 pt-3 border-t border-[#E4E1D8] text-xs text-[#5B5B62] space-y-1.5"
                  >
                    <div><strong className="text-navy">Pourquoi :</strong> {comprehension.why_fr}</div>
                    <div><strong className="text-navy">Confiance :</strong> {Math.round((comprehension.confidence || 0) * 100)}%</div>
                    <div><strong className="text-navy">Données utilisées :</strong> {(comprehension.data_used || []).slice(0, 5).join(", ")}{(comprehension.data_used || []).length > 5 ? "…" : ""}</div>
                    <div><strong className="text-navy">Sources :</strong> {(comprehension.sources_fr || []).join(" · ")}</div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Axes multidimensionnels */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
          <div className="border-2 border-[#E5E7EB] bg-white rounded-lg p-6" data-testid="report-axes">
            <h2 className="font-display text-xl text-navy mb-4">8 axes multidimensionnels</h2>
            <div className="space-y-3">
              {Object.entries(AXIS_LABELS).map(([k, label]) => (
                <AxisBar key={k} label={label} value={s[k] || 0} />
              ))}
            </div>
          </div>

          <div className="border-2 border-[#E5E7EB] bg-white rounded-lg p-6" data-testid="report-indices">
            <h2 className="font-display text-xl text-navy mb-4">Indices synthétiques</h2>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(INDEX_LABELS).map(([k, label]) => (
                <IndexCard key={k} label={label} value={s[k] || 0} isRisk={k === "idx_risk"} />
              ))}
            </div>
          </div>
        </div>

        {/* Scénarios */}
        {scenarios.length > 0 && (
          <div className="mb-10" data-testid="report-scenarios">
            <h2 className="font-display text-2xl text-navy mb-4">4 trajectoires projetées</h2>
            <p className="text-sm text-[#5B5B62] mb-6 max-w-3xl">Projections calculées à partir de vos données actuelles et de votre capacité d&apos;action déclarée. Horizons 1/3/5/10 ans.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {scenarios.map((sc) => <ScenarioCard key={sc.key} scenario={sc} />)}
            </div>
          </div>
        )}

        {/* AI Analysis */}
        {ai.generated_at ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10" data-testid="report-ai">
            <AnalysisBlock icon={CheckCircle2} title="Forces" items={ai.strengths_fr} tone="emerald" />
            <AnalysisBlock icon={AlertTriangle} title="Vulnérabilités" items={ai.vulnerabilities_fr} tone="amber" />
            <AnalysisBlock icon={Sparkles} title="Opportunités" items={ai.opportunities_fr} tone="blue" />
            <AnalysisBlock icon={Shield} title="Risques" items={ai.risks_fr} tone="red" />
            <AnalysisBlock icon={Target} title="Priorités" items={ai.priorities_fr} tone="navy" wide />
            <AnalysisBlock icon={Zap} title="Gains rapides" items={ai.quick_wins_fr} tone="emerald" />
            <AnalysisBlock icon={TrendingUp} title="Gains long terme" items={ai.long_term_wins_fr} tone="blue" />
            <AnalysisBlock icon={ChevronRight} title="Recommandations" items={ai.recommendations_fr} tone="navy" wide />
            {(ai.recommended_partners_fr?.length > 0) && (
              <AnalysisBlock icon={Users} title="Partenaires suggérés" items={ai.recommended_partners_fr} tone="gold" />
            )}
            {(ai.recommended_programs_fr?.length > 0) && (
              <AnalysisBlock icon={Award} title="Programmes gouvernementaux" items={ai.recommended_programs_fr} tone="gold" />
            )}
            {(ai.recommended_trainings_fr?.length > 0) && (
              <AnalysisBlock icon={GraduationCap} title="Formations recommandées" items={ai.recommended_trainings_fr} tone="gold" />
            )}
            {(ai.missing_info_fr?.length > 0) && (
              <AnalysisBlock icon={MessageSquare} title="Informations à préciser" items={ai.missing_info_fr} tone="grey" />
            )}
          </div>
        ) : (
          <div className="border-2 border-dashed border-[#E5E7EB] bg-white rounded-lg p-8 text-center mb-10">
            <Sparkles className="h-8 w-8 mx-auto text-[#8B8B8F] mb-3" />
            <p className="text-sm text-[#5B5B62]">L&apos;analyse IA n&apos;a pas pu être générée. {ai.error_fr || ""}</p>
            <Link to="/app/diagnostic" className="mt-4 inline-block text-navy underline text-sm">Retenter</Link>
          </div>
        )}

        {/* Matches — real network members & orgs */}
        {matches && (matches.mentors?.length > 0 || matches.organizations?.length > 0 || matches.opportunities?.length > 0) && (
          <div className="mb-10" data-testid="report-matches">
            <h2 className="font-display text-2xl text-navy mb-4">Écosystème pour vous</h2>
            <p className="text-sm text-[#5B5B62] mb-6 max-w-3xl">
              Vrais professionnels et organismes de l&apos;écosystème ALTIORA COOP qui correspondent à votre profil.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {matches.mentors?.slice(0, 3).map((m) => (
                <div key={m.id} className="border-2 border-navy/25 bg-white rounded-lg p-4" data-testid={`match-mentor-${m.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-navy" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-navy">{m.role_network}</span>
                  </div>
                  <div className="font-display text-base text-navy leading-tight">{m.display_name}</div>
                  <div className="mt-1 text-xs text-[#374151] leading-snug line-clamp-2">{m.headline_fr}</div>
                  {m.linkedin_url && (
                    <a href={m.linkedin_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-navy hover:underline">
                      LinkedIn <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
              {matches.organizations?.slice(0, 3).map((o) => (
                <div key={o.id} className="border-2 border-[#C5A880] bg-[#FBF6EA]/40 rounded-lg p-4" data-testid={`match-org-${o.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="h-4 w-4 text-[#7A5B14]" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#7A5B14]">Organisation</span>
                  </div>
                  <div className="font-display text-base text-[#7A5B14] leading-tight">{o.name_fr}</div>
                  <div className="mt-1 text-xs text-[#374151] leading-snug line-clamp-2">{o.description_fr}</div>
                  {o.url && (
                    <a href={o.url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-[#7A5B14] hover:underline">
                      Site <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
              {matches.opportunities?.slice(0, 3).map((o) => (
                <div key={o.id} className="border-2 border-emerald-300 bg-emerald-50/60 rounded-lg p-4" data-testid={`match-opp-${o.id}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="h-4 w-4 text-emerald-700" />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Opportunité</span>
                  </div>
                  <div className="font-display text-base text-emerald-800 leading-tight">{o.name_fr}</div>
                  <div className="mt-1 text-xs text-[#374151] leading-snug line-clamp-2">{o.description_fr}</div>
                </div>
              ))}
            </div>
            {matches.search_tags?.length > 0 && (
              <div className="mt-4 text-[11px] font-mono text-[#8B8B8F]">
                Tags de recherche : {matches.search_tags.slice(0, 10).join(" · ")}
              </div>
            )}
          </div>
        )}

        {/* Legal disclaimer */}
        <div className="border border-dashed border-[#C5A880]/60 bg-[#FBF6EA]/70 rounded-md p-4 flex items-start gap-2 mb-6">
          <ShieldAlert className="h-4 w-4 text-[#7A5B14] flex-shrink-0 mt-0.5" strokeWidth={2} />
          <div className="text-xs text-[#7A5B14] leading-relaxed">{data.legal_disclaimer_fr}</div>
        </div>
      </div>
    </div>
  );
}

function DimensionBar({ label, value, weight, tone }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-[#374151] font-medium">{label}</span>
        <span className="text-[#8B8B8F] font-mono tabular-nums">{value} / 100 · {weight}</span>
      </div>
      <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${tone} transition-all`} style={{ width: `${Math.max(2, value)}%` }} />
      </div>
    </div>
  );
}

function AxisBar({ label, value }) {
  const tone = value >= 70 ? "bg-emerald-500" : value >= 50 ? "bg-navy" : value >= 30 ? "bg-amber-500" : "bg-red-500";
  return (
    <div>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-[#1F2937] font-medium">{label}</span>
        <span className="text-[#5B5B62] font-mono tabular-nums">{value}</span>
      </div>
      <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
        <div className={`h-full ${tone} transition-all`} style={{ width: `${Math.max(3, value)}%` }} />
      </div>
    </div>
  );
}

function IndexCard({ label, value, isRisk }) {
  const good = isRisk ? value < 40 : value >= 60;
  const bad = isRisk ? value >= 70 : value < 40;
  const tone = good ? "border-emerald-300 bg-emerald-50 text-emerald-900" : bad ? "border-red-300 bg-red-50 text-red-900" : "border-[#E5E7EB] bg-[#F8F7F3] text-navy";
  return (
    <div className={`border rounded-md p-3 ${tone}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="font-display text-2xl mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function ScenarioCard({ scenario }) {
  const p10 = scenario.projections?.["10"] || {};
  const p5 = scenario.projections?.["5"] || {};
  const toneMap = {
    pessimiste: "border-red-300 bg-red-50/40",
    realiste: "border-navy/25 bg-navy-50/40",
    optimiste: "border-emerald-300 bg-emerald-50/40",
    ambitieux: "border-[#C5A880] bg-[#FBF6EA]/60",
  };
  return (
    <div className={`border-2 rounded-lg p-5 ${toneMap[scenario.key] || "border-[#E5E7EB] bg-white"}`} data-testid={`scenario-${scenario.key}`}>
      <div className="font-display text-lg text-navy mb-1">{scenario.label_fr}</div>
      <div className="text-xs text-[#5B5B62] leading-snug mb-4">{scenario.narrative_fr}</div>
      <div className="space-y-2 text-xs">
        <div><span className="text-[#8B8B8F]">Revenu 5 ans :</span> <span className="font-mono text-[#1F2937] font-bold tabular-nums">{formatCad(p5.projected_income_cad)}</span></div>
        <div><span className="text-[#8B8B8F]">Revenu 10 ans :</span> <span className="font-mono text-[#1F2937] font-bold tabular-nums">{formatCad(p10.projected_income_cad)}</span></div>
        <div><span className="text-[#8B8B8F]">Valeur nette 10 ans :</span> <span className="font-mono text-[#1F2937] font-bold tabular-nums">{formatCad(p10.projected_net_worth_cad)}</span></div>
        <div className="pt-2 mt-2 border-t border-current/10 flex justify-between text-[10px] font-mono uppercase tracking-widest">
          <span>Autonomie {scenario.autonomy_index}</span>
          <span>Risque {scenario.risk_index}</span>
        </div>
      </div>
    </div>
  );
}

function AnalysisBlock({ icon: Icon, title, items = [], tone, wide }) {
  if (!items || items.length === 0) return null;
  const toneMap = {
    emerald: "border-emerald-300 bg-white text-emerald-900",
    amber:   "border-amber-300 bg-white text-amber-900",
    blue:    "border-blue-300 bg-white text-blue-900",
    red:     "border-red-300 bg-white text-red-900",
    navy:    "border-navy/40 bg-white text-navy",
    gold:    "border-[#C5A880] bg-white text-[#7A5B14]",
    grey:    "border-[#E5E7EB] bg-white text-[#5B5B62]",
  };
  return (
    <div className={`border-2 rounded-lg p-5 ${toneMap[tone] || toneMap.navy} ${wide ? "md:col-span-2" : ""}`}>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-4 w-4" strokeWidth={2} />
        <h3 className="font-display text-base">{title}</h3>
        <span className="ml-auto text-[10px] font-mono opacity-60">{items.length}</span>
      </div>
      <ul className="space-y-2 text-sm leading-relaxed">
        {items.map((item, i) => <li key={i} className="flex gap-2"><span className="opacity-40">•</span><span>{item}</span></li>)}
      </ul>
    </div>
  );
}

function formatCad(v) {
  if (v == null || isNaN(v)) return "—";
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 }).format(v);
}
